import { Routes } from '@angular/router';
import { RenderMode, ServerRoute } from '@angular/ssr';
import { PRIVILEGE_KEYS } from 'app/core/services/authorization/privilege-keys';
import { environment } from 'environments/environment';

const LIGHT_POINT_PARAMETER_ROUTE_DATA = [
  ['light-point-arm-types', 'Tipos de Braço', 'Tipo de braço', 'tipo de braço'],
  ['light-point-pole-types', 'Tipos de Poste', 'Tipo de poste', 'tipo de poste'],
  ['light-point-pole-properties', 'Propriedades de Poste', 'Propriedade do poste', 'propriedade do poste'],
  ['light-point-luminaire-types', 'Tipos de Luminária', 'Tipo de luminária', 'tipo de luminária'],
  ['light-point-cables', 'Cabos', 'Cabo', 'cabo'],
  ['light-point-sockets', 'Soquetes', 'Soquete', 'soquete'],
  ['light-point-hardwares', 'Ferragens', 'Ferragem', 'ferragem'],
  ['light-point-reactor-types', 'Tipos de Reator', 'Tipo de reator', 'tipo de reator'],
  ['light-point-reactor-powers', 'Potências de Reator', 'Potência de reator', 'potência de reator'],
  ['light-point-relays', 'Relés', 'Relé', 'relé'],
  ['light-point-connection-types', 'Tipos de Conexão', 'Tipo de conexão', 'tipo de conexão'],
  ['light-point-connection-gauges', 'Bitolas de Conexão', 'Bitola de conexão', 'bitola de conexão'],
  ['light-point-lamp-features', 'Características de Lâmpada', 'Característica da lâmpada', 'característica da lâmpada'],
  ['light-point-lamp-powers', 'Potências de Lâmpada', 'Potência da lâmpada', 'potência da lâmpada'],
  ['light-point-lamp-types', 'Tipos de Lâmpada', 'Tipo de lâmpada', 'tipo de lâmpada'],
  ['light-point-inconsistencies', 'Inconsistências', 'Inconsistência', 'inconsistência'],
] as const;

function parameterRouteData(item: (typeof LIGHT_POINT_PARAMETER_ROUTE_DATA)[number]) {
  const [path, pluralLabel, singularLabel, singularLower] = item;
  return {
    path,
    endpoint: path,
    breadcrumb: pluralLabel,
    title: `Lista de ${pluralLabel}`,
    singularLabel,
    singularLower,
    pluralLabel,
    namePlaceholder: singularLabel,
    accountPlaceholder: `Selecione a conta referente a ${pluralLabel.toLocaleLowerCase('pt-BR')}`,
    descriptionText: `Cadastre ${pluralLabel.toLocaleLowerCase('pt-BR')} usados nos pontos luminosos.`,
    baseRoute: `/public-illumination/${path}`,
    emptyMessage: 'Nenhum item encontrado. Clique para cadastrar.',
    requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView,
  };
}

const lightPointParameterRoutes: Routes = LIGHT_POINT_PARAMETER_ROUTE_DATA.flatMap((item) => {
  const data = parameterRouteData(item);
  return [
    {
      path: item[0],
      loadComponent: () => import('./light-point-parameters/list-light-point-parameters.component').then((m) => m.ListLightPointParametersComponent),
      data,
    },
    {
      path: `${item[0]}/add`,
      loadComponent: () => import('./light-point-parameters/light-point-parameter-form.component').then((m) => m.LightPointParameterFormComponent),
      data: {
        ...data,
        breadcrumb: `Cadastrar ${item[2]}`,
        mode: 'create',
        requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate,
      },
    },
    {
      path: `${item[0]}/edit/:accountId/:id`,
      loadComponent: () => import('./light-point-parameters/light-point-parameter-form.component').then((m) => m.LightPointParameterFormComponent),
      data: {
        ...data,
        breadcrumb: `Editar ${item[2]}`,
        mode: 'edit',
        requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsUpdate,
      },
    },
  ];
});

export const PUBLIC_ILLUMINATION_ROUTES: Routes = [
  {
    path: 'dashboard',
    loadComponent: () => import('./dashboard/dashboard-public-illumination/dashboard-public-illumination.component').then((m) => m.DashboardPublicIlluminationComponent),
    data: { breadcrumb: 'Dashboard', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.dashboardView },
  },
  {
    path: 'map',
    loadComponent: () => import('./map-public-illumination/map-public-illumination.component').then((m) => m.MapPublicIlluminationComponent),
    data: { breadcrumb: 'Mapa', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.mapView },
  },
  ...(environment.production ? [] : [{
    path: 'point-reconciliation',
    loadComponent: () => import('./point-reconciliation/point-reconciliation.component').then((m) => m.PointReconciliationComponent),
    data: { breadcrumb: 'Vinculação SIPUB × base oficial', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.mapView },
  }]),
  {
    path: 'service-orders',
    loadComponent: () => import('./service-order/list-service-orders/list-service-orders.component').then((m) => m.ListServiceOrdersComponent),
    data: { breadcrumb: 'Ordens de Serviço', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersView },
  },
  {
    path: 'service-orders/add',
    loadComponent: () => import('./service-order/create-service-order/create-service-order.component').then((m) => m.CreateServiceOrderComponent),
    data: { breadcrumb: 'Criar Ordem de Serviço', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersCreate },
  },
  {
    path: 'service-orders/edit/:accountId/:id',
    loadComponent: () => import('./service-order/create-service-order/create-service-order.component').then((m) => m.CreateServiceOrderComponent),
    data: { breadcrumb: 'Editar Ordem de Serviço', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersUpdate },
  },
  {
    path: 'service-order-teams',
    loadComponent: () => import('./service-order/teams/list-service-order-teams/list-service-order-teams.component').then((m) => m.ListServiceOrderTeamsComponent),
    data: { breadcrumb: 'Turmas', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersView },
  },
  {
    path: 'service-order-teams/add',
    loadComponent: () => import('./service-order/teams/create-service-order-team/create-service-order-team.component').then((m) => m.CreateServiceOrderTeamComponent),
    data: { breadcrumb: 'Cadastrar Turma', mode: 'create', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersCreate },
  },
  {
    path: 'service-order-teams/edit/:accountId/:id',
    loadComponent: () => import('./service-order/teams/create-service-order-team/create-service-order-team.component').then((m) => m.CreateServiceOrderTeamComponent),
    data: { breadcrumb: 'Editar Turma', mode: 'edit', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersUpdate },
  },
  {
    path: 'service-order-causes',
    loadComponent: () => import('./service-order/parameters/list-service-order-parameters/list-service-order-parameters.component').then((m) => m.ListServiceOrderParametersComponent),
    data: { breadcrumb: 'Causas de OS', parameterType: 'cause', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersView },
  },
  {
    path: 'service-order-causes/add',
    loadComponent: () => import('./service-order/parameters/create-service-order-parameter/create-service-order-parameter.component').then((m) => m.CreateServiceOrderParameterComponent),
    data: { breadcrumb: 'Cadastrar Causa', mode: 'create', parameterType: 'cause', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersCreate },
  },
  {
    path: 'service-order-causes/edit/:accountId/:id',
    loadComponent: () => import('./service-order/parameters/create-service-order-parameter/create-service-order-parameter.component').then((m) => m.CreateServiceOrderParameterComponent),
    data: { breadcrumb: 'Editar Causa', mode: 'edit', parameterType: 'cause', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersUpdate },
  },
  {
    path: 'service-order-motives',
    loadComponent: () => import('./service-order/parameters/list-service-order-parameters/list-service-order-parameters.component').then((m) => m.ListServiceOrderParametersComponent),
    data: { breadcrumb: 'Motivos de OS', parameterType: 'motive', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersView },
  },
  {
    path: 'service-order-motives/add',
    loadComponent: () => import('./service-order/parameters/create-service-order-parameter/create-service-order-parameter.component').then((m) => m.CreateServiceOrderParameterComponent),
    data: { breadcrumb: 'Cadastrar Motivo', mode: 'create', parameterType: 'motive', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersCreate },
  },
  {
    path: 'service-order-motives/edit/:accountId/:id',
    loadComponent: () => import('./service-order/parameters/create-service-order-parameter/create-service-order-parameter.component').then((m) => m.CreateServiceOrderParameterComponent),
    data: { breadcrumb: 'Editar Motivo', mode: 'edit', parameterType: 'motive', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersUpdate },
  },
  {
    path: 'spent-materials',
    loadComponent: () => import('./service-order/materials/spent-material-catalog.component').then((m) => m.SpentMaterialCatalogComponent),
    data: { breadcrumb: 'Materiais Gastos', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.serviceOrdersView },
  },
  {
    path: 'transformer-nominal-voltages',
    loadComponent: () => import('./transformer-parameters/nominal-voltage/list-transformer-nominal-voltages.component').then((m) => m.ListTransformerNominalVoltagesComponent),
    data: { breadcrumb: 'Tensões Nominais do Transformador', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView },
  },
  {
    path: 'transformer-nominal-voltages/add',
    loadComponent: () => import('./transformer-parameters/nominal-voltage/transformer-nominal-voltage-form.component').then((m) => m.TransformerNominalVoltageFormComponent),
    data: { breadcrumb: 'Cadastrar Tensão Nominal', mode: 'create', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'transformer-nominal-voltages/edit/:accountId/:id',
    loadComponent: () => import('./transformer-parameters/nominal-voltage/transformer-nominal-voltage-form.component').then((m) => m.TransformerNominalVoltageFormComponent),
    data: { breadcrumb: 'Editar Tensão Nominal', mode: 'edit', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsUpdate },
  },
  {
    path: 'transformer-powers',
    loadComponent: () => import('./transformer-parameters/power/list-transformer-powers.component').then((m) => m.ListTransformerPowersComponent),
    data: { breadcrumb: 'Potências do Transformador', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView },
  },
  {
    path: 'transformer-powers/add',
    loadComponent: () => import('./transformer-parameters/power/transformer-power-form.component').then((m) => m.TransformerPowerFormComponent),
    data: { breadcrumb: 'Cadastrar Potência', mode: 'create', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'transformer-powers/edit/:accountId/:id',
    loadComponent: () => import('./transformer-parameters/power/transformer-power-form.component').then((m) => m.TransformerPowerFormComponent),
    data: { breadcrumb: 'Editar Potência', mode: 'edit', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsUpdate },
  },
  {
    path: 'transformer-connections',
    loadComponent: () => import('./transformer-parameters/connection/list-transformer-connections.component').then((m) => m.ListTransformerConnectionsComponent),
    data: { breadcrumb: 'Ligações do Transformador', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView },
  },
  {
    path: 'transformer-connections/add',
    loadComponent: () => import('./transformer-parameters/connection/transformer-connection-form.component').then((m) => m.TransformerConnectionFormComponent),
    data: { breadcrumb: 'Cadastrar Ligação', mode: 'create', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'transformer-connections/edit/:accountId/:id',
    loadComponent: () => import('./transformer-parameters/connection/transformer-connection-form.component').then((m) => m.TransformerConnectionFormComponent),
    data: { breadcrumb: 'Editar Ligação', mode: 'edit', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsUpdate },
  },
  {
    path: 'transformer-voltage-classes',
    loadComponent: () => import('./transformer-parameters/voltage-class/list-transformer-voltage-classes.component').then((m) => m.ListTransformerVoltageClassesComponent),
    data: { breadcrumb: 'Classes de Tensão do Transformador', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView },
  },
  {
    path: 'transformer-voltage-classes/add',
    loadComponent: () => import('./transformer-parameters/voltage-class/transformer-voltage-class-form.component').then((m) => m.TransformerVoltageClassFormComponent),
    data: { breadcrumb: 'Cadastrar Classe de Tensão', mode: 'create', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'transformer-voltage-classes/edit/:accountId/:id',
    loadComponent: () => import('./transformer-parameters/voltage-class/transformer-voltage-class-form.component').then((m) => m.TransformerVoltageClassFormComponent),
    data: { breadcrumb: 'Editar Classe de Tensão', mode: 'edit', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsUpdate },
  },
  {
    path: 'transformer-properties',
    loadComponent: () => import('./transformer-parameters/property/list-transformer-properties.component').then((m) => m.ListTransformerPropertiesComponent),
    data: { breadcrumb: 'Propriedades do Transformador', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView },
  },
  {
    path: 'transformer-properties/add',
    loadComponent: () => import('./transformer-parameters/property/transformer-property-form.component').then((m) => m.TransformerPropertyFormComponent),
    data: { breadcrumb: 'Cadastrar Propriedade', mode: 'create', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'transformer-properties/edit/:accountId/:id',
    loadComponent: () => import('./transformer-parameters/property/transformer-property-form.component').then((m) => m.TransformerPropertyFormComponent),
    data: { breadcrumb: 'Editar Propriedade', mode: 'edit', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsUpdate },
  },
  {
    path: 'transformer-materials',
    loadComponent: () => import('./transformer-parameters/material/list-transformer-materials.component').then((m) => m.ListTransformerMaterialsComponent),
    data: { breadcrumb: 'Materiais do Transformador', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView },
  },
  {
    path: 'transformer-materials/add',
    loadComponent: () => import('./transformer-parameters/material/transformer-material-form.component').then((m) => m.TransformerMaterialFormComponent),
    data: { breadcrumb: 'Cadastrar Material', mode: 'create', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'transformer-materials/edit/:accountId/:id',
    loadComponent: () => import('./transformer-parameters/material/transformer-material-form.component').then((m) => m.TransformerMaterialFormComponent),
    data: { breadcrumb: 'Editar Material', mode: 'edit', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsUpdate },
  },
  ...lightPointParameterRoutes,
  {
    path: 'add-drive-by',
    loadComponent: () => import('./create-point-drive-by/create-point-drive-by.component').then((m) => m.CreatePointDriveByComponent),
    data: { breadcrumb: 'Cadastro de Ponto Embarcado', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'list-cameras',
    loadComponent: () => import('./camera-list/camera-list.component').then((m) => m.CameraListComponent),
    data: { breadcrumb: 'Lista de câmeras', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.cameraList },
  },
  {
    path: 'reports',
    loadComponent: () => import('./reports/public-illumination-reports.component').then((m) => m.PublicIlluminationReportsComponent),
    data: { breadcrumb: 'Relatórios', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.reportsView },
  },
  {
    path: 'audit-center',
    loadComponent: () => import('./audit-center/audit-center.component').then((m) => m.AuditCenterComponent),
    data: { breadcrumb: 'Central de Auditoria', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.reportsView },
  },
  {
    path: 'report-parameters',
    data: { breadcrumb: 'Parâmetros dos Relatórios', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.reportsView },
    children: [
      {
        path: '',
        loadComponent: () => import('./report-parameters/report-parameters.component').then((m) => m.ReportParametersComponent),
      },
      {
        path: 'reactor-loss',
        loadComponent: () => import('./report-parameters/reactor-loss/reactor-loss.component').then((m) => m.ReactorLossComponent),
        data: { breadcrumb: 'Perda de Reator', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.reportsView },
      },
    ],
  },
  {
    path: 'neighborhoods',
    loadComponent: () => import('./neighborhoods/neighborhoods.component').then((m) => m.NeighborhoodsComponent),
    data: { breadcrumb: 'Bairros', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsView },
  },
  {
    path: 'qr-code-generator',
    loadComponent: () => import('./qr-code-generator/qr-code-generator.component').then((m) => m.QrCodeGeneratorComponent),
    data: { breadcrumb: 'Gerador de QR Codes', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.pointsCreate },
  },
  {
    path: 'snitch',
    data: { breadcrumb: 'Snitch', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.embeddedView },
    children: [
      {
        path: 'map',
        loadComponent: () => import('./snitch/snitch-map/snitch-map.component').then((m) => m.SnitchMapComponent),
        data: { breadcrumb: 'Mapa Snitch', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.embeddedView },
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./snitch/snitch-dashboard/snitch-dashboard.component').then((m) => m.SnitchDashboardComponent),
        data: { breadcrumb: 'Dashboards Snitch', requiredPrivilege: PRIVILEGE_KEYS.publicIllumination.embeddedView },
      },
      { path: '', redirectTo: 'map', pathMatch: 'full' },
    ],
  },
];

export const PUBLIC_ILLUMINATION_SERVER_ROUTES: ServerRoute[] = [
  'service-orders',
  'service-order-teams',
  'service-order-causes',
  'service-order-motives',
  'transformer-nominal-voltages',
  'transformer-powers',
  'transformer-connections',
  'transformer-voltage-classes',
  'transformer-properties',
  'transformer-materials',
].map((path) => ({
  path: `public-illumination/${path}/edit/:accountId/:id`,
  renderMode: RenderMode.Server,
}));

PUBLIC_ILLUMINATION_SERVER_ROUTES.push(
  ...LIGHT_POINT_PARAMETER_ROUTE_DATA.map<ServerRoute>((item) => ({
    path: `public-illumination/${item[0]}/edit/:accountId/:id`,
    renderMode: RenderMode.Server,
  })),
);
